import { Box, Button, TextField, Typography, Select, MenuItem, FormControl, Dialog, DialogContent, DialogActions, Card, CardContent, Stack } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { useState, useEffect } from "react";
import moment from "moment";
import { doc, updateDoc } from "firebase/firestore";
import { useDocument } from "react-firebase-hooks/firestore";
import { useAuth } from "contexts/User";
import { DatePicker } from "@mui/x-date-pickers";
import { useFirebase } from "contexts/Firebase";
import { useNotifications } from "contexts/Notification";
export interface CoveredPerson {
    dob?: string;
    relationship?: string;
    gender?: string;
    pregnant?: boolean;
    tobacco?: boolean;
    name?: string;
}
interface Errors {
    name?: string;
    dob?: string;
    gender?: string;
    relationship?: string;
}

function HouseholdMembers(props: { saved: Function, cancel?: Function, saveOverride?: (members: { [key: string]: CoveredPerson }) => any, userId?: string }) {
    const { firestore } = useFirebase();
    const { user } = useAuth();
    const [step, setStep] = useState<string>("overview");
    const userAnswersDoc = user ? doc(firestore, 'users', props.userId || user?.uid!, "answers", props.userId || user?.uid!) : null;
    const [answers, answersLoading, firestoreError] = useDocument(
        userAnswersDoc,
        {
            snapshotListenOptions: { includeMetadataChanges: true },
        }
    );
    const [members, setMembers] = useState<{ [key: string]: CoveredPerson }>({});
    const [currentMemberName, setCurrentMemberName] = useState<any>({});
    const [currentMember, setCurrentMember] = useState<CoveredPerson>({});
    const [errors, setErrors] = useState<Errors>({});
    const { notifications } = useNotifications();

    useEffect(() => {
        const mems = answers?.get('insuranceDetails.household.members');
        if (!answersLoading && mems === undefined) {
            console.log("members is undefined")
            // create members
            updateDoc(answers?.ref!, {
                "insuranceDetails.household.members": {}
            });
        } else {
            console.log("set members")
            setMembers(mems);
        }
        console.log(answers?.data())
    }, [answers, answersLoading])
    useEffect(() => {
        if (firestoreError) {
            console.error(firestoreError);
        }
    })
    function addMember() {
        setCurrentMember({});
        setCurrentMemberName("");
        setStep("editMember");
    }
    function editMember(name: string) {
        console.log(`edit ${name} `, members[name])
        setCurrentMember(members[name]);
        setCurrentMemberName(name);
        setStep("editMember");
    }
    function deleteMember(name: string) {
        let copy = { ...members };
        //delete old record
        delete copy[name];
        setMembers({ ...copy });
    }
    function saveMember() {
        let copy = { ...members };
        if (currentMemberName !== currentMember.name) {
            //delete old record
            delete copy[currentMemberName];
        }
        setMembers({ ...copy, [currentMember.name!]: currentMember });
        setStep("overview");

    }
    const handleMemberValidationState = () => {
        const newErrors :Errors= {};
        if (!currentMember.name) newErrors.name = 'Name is required';
        if (!currentMember.dob) newErrors.dob = 'Date of Birth is required';
        if (!currentMember.gender) newErrors.gender = 'Gender is required';
        if (!currentMember.relationship) newErrors.relationship = 'Relationship is required';

        setErrors(newErrors);

        if (Object.keys(newErrors).length === 0) {
            saveMember();
        }
    };
    async function save() {
        try {
            const hasSelfMember = Object.values(members).some(member => member.relationship === 'self');
            if (!hasSelfMember) {
                notifications.error("You must have one household member with the relationship as 'self' to indicate who the primary policyholder is.");
                return;
            }

            if (typeof props.saveOverride === "function") {
                props.saveOverride(members);
            } else {
                await updateDoc(userAnswersDoc!, {
                    "insuranceDetails.household.members": members
                });
                if (typeof props.saved === "function") {
                    props.saved();
                }
            }
            return true;
        }
        catch (err: any) {
            console.error(err)
        }
    }
    function cancelEdit() {
        setStep("overview");
        setErrors({});
    }

    function removeMember() {
        if (currentMemberName) {
            deleteMember(currentMemberName);
            setStep("overview");
        }
    }
    
    // Convert date from MM/DD/YYYY to text format like "May 12, 1998"
    const formatDate = (dateStr?: string) => {
        if (!dateStr) return '';
        return moment(dateStr, "MM/DD/YYYY").format("MMMM D, YYYY");
    };

    // Get first letter of member's name for avatar
    // const getInitial = (name?: string) => {
    //     if (!name) return '';
    //     return name.charAt(0).toUpperCase();
    // };

    // Capitalize first letter of string
    const capitalize = (str?: string) => {
        if (!str) return '';
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    };
    
    if (members === undefined) {
        return (
            <Typography>Loading</Typography>
        )
    }

    // Render edit member dialog
    const renderEditMemberDialog = () => {
        if (step !== "editMember" || !currentMember) return null;
        
        return (
          <Dialog 
            open={true} 
            onClose={cancelEdit}
            maxWidth="xs"
            fullWidth
          >
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              p: 2,
              pb: 1,
              borderBottom: '1px solid #eee'
            }}>
              <ArrowBackIcon 
                sx={{ cursor: 'pointer' }} 
                onClick={cancelEdit}
              />
              <Typography 
                sx={{ 
                  fontWeight: 600,
                  fontSize: '1.25rem',
                  lineHeight: 1.6
                }}
              >
                Edit Member
              </Typography>
              <CloseIcon 
                sx={{ cursor: 'pointer', ml: 'auto' }} 
                onClick={cancelEdit}
              />
            </Box>
            <DialogContent sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "24px",
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>Name</Typography>
                  <TextField
                    data-testid="name-input"
                    value={currentMember.name || ""}
                    onChange={(e) =>
                      setCurrentMember({ ...currentMember, name: e.target.value })
                    }
                    fullWidth
                    variant="outlined"
                    error={!!errors.name}
                    helperText={errors.name}
                  />
                </Box>
                
                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>Relationship</Typography>
                  <FormControl fullWidth error={!!errors.relationship}>
                    <Select
                      data-testid="relationship-select"
                      value={currentMember.relationship || ""}
                      onChange={(e) =>
                        setCurrentMember({
                          ...currentMember,
                          relationship: e.target.value,
                        })
                      }
                      displayEmpty
                      variant="outlined"
                    >
                      <MenuItem value="" disabled>
                        Select relationship
                      </MenuItem>
                      {Object.values(members).some(
                        (member) => member.relationship === "self"
                      ) && currentMember.relationship !== "self" ? null : (
                        <MenuItem
                          data-testid="self-option"
                          value={"self"}
                          disabled={Object.values(members).some(
                            (member) => member.relationship === "self" && member.name !== currentMemberName
                          )}
                        >
                          Self
                        </MenuItem>
                      )}
                      <MenuItem
                        data-testid="spouse-option"
                        value={"spouse"}
                        disabled={Object.values(members).some(
                          (member) => member.relationship === "spouse" && member.name !== currentMemberName
                        )}
                      >
                        Spouse
                      </MenuItem>
                      <MenuItem data-testid="child-option" value={"child"}>
                        Child
                      </MenuItem>
                      <MenuItem value={"sibling"}>
                        Sibling
                      </MenuItem>
                      <MenuItem value={"parent"}>
                        Parent
                      </MenuItem>
                      <MenuItem value={"other"}>
                        Other
                      </MenuItem>
                    </Select>
                    {errors.relationship && (
                      <Typography color="error" variant="caption">{errors.relationship}</Typography>
                    )}
                  </FormControl>
                </Box>
                
                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>Gender</Typography>
                  <FormControl fullWidth error={!!errors.gender}>
                    <Select
                      data-testid="gender-select"
                      value={currentMember.gender || ""}
                      onChange={(e) =>
                        setCurrentMember({
                          ...currentMember,
                          gender: e.target.value,
                        })
                      }
                      displayEmpty
                      variant="outlined"
                    >
                      <MenuItem value="" disabled>
                        Select gender
                      </MenuItem>
                      <MenuItem value="female">Female</MenuItem>
                      <MenuItem value="male">Male</MenuItem>
                    </Select>
                    {errors.gender && (
                      <Typography color="error" variant="caption">{errors.gender}</Typography>
                    )}
                  </FormControl>
                </Box>
                
                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>Date of Birth</Typography>
                  <DatePicker
                    data-testid="dob-input"
                    value={currentMember.dob ? moment(currentMember.dob, "MM/DD/YYYY") : null}
                    onChange={(date) =>
                      date != null
                        ? setCurrentMember({
                            ...currentMember,
                            dob: moment(date).format("MM/DD/YYYY"),
                          })
                        : null
                    }
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: 'outlined',
                        error: !!errors.dob,
                        helperText: errors.dob
                      }
                    }}
                  />
                </Box>
                
                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>Tobacco User?</Typography>
                  <FormControl fullWidth>
                    <Select
                      value={currentMember.tobacco ? "Yes" : "No"}
                      onChange={(e) =>
                        setCurrentMember({
                          ...currentMember,
                          tobacco: e.target.value === "Yes",
                        })
                      }
                      displayEmpty
                      variant="outlined"
                    >
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                
                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>Pregnant?</Typography>
                  <FormControl fullWidth>
                    <Select
                      value={currentMember.pregnant ? "Yes" : "No"}
                      onChange={(e) =>
                        setCurrentMember({
                          ...currentMember,
                          pregnant: e.target.value === "Yes",
                        })
                      }
                      displayEmpty
                      variant="outlined"
                      disabled={currentMember.gender === "male"}
                    >
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ 
              p: 2,
              display: 'flex',
              justifyContent: 'space-between',
              borderTop: '1px solid #eee'
            }}>
              <Button
                variant="outlined"
                onClick={removeMember}
                color="error"
                disabled={currentMember.relationship === "self"}
              >
                Remove
              </Button>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  onClick={cancelEdit}
                >
                  Cancel
                </Button>
                <Button
                  data-testid="save-button"
                  variant="contained"
                  onClick={handleMemberValidationState}
                  color="primary"
                >
                  Save
                </Button>
              </Box>
            </DialogActions>
          </Dialog>
        );
    };

    // Render household overview dialog
    const renderHouseholdDialog = () => {
        if (step !== "overview") return null;
        
        return (
          <Dialog 
            open={true} 
            onClose={() => props && props.cancel && props.cancel(false)}
            maxWidth="sm"
            fullWidth
          >
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                p: 2,
                pb: 1,
                borderBottom: '1px solid #eee'
              }}>
                <Typography 
                  sx={{ 
                    fontWeight: 600,
                    fontSize: '1.25rem',
                    lineHeight: 1.6
                  }}
                >
                  Update household
                </Typography>
                <CloseIcon 
                  sx={{ cursor: 'pointer' }} 
                  onClick={() => props && props.cancel && props.cancel(false)}
                />
              </Box>
              <DialogContent sx={{ p: 3 }}>
                  <Stack spacing={2}>
                      {Object.entries(members).map(([key, member], index) => {
                          if (key === 'undefined' || Object.keys(member).length === 0) {
                              return null;
                          }
                          
                          return (
                              <Card 
                                  key={key} 
                                  variant="outlined" 
                                  sx={{ 
                                      borderRadius: 1,
                                      boxShadow: 'none'
                                  }}
                              >
                                  <CardContent sx={{ 
                                      p: '16px !important',
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center'
                                  }}>
                                      <Box>
                                          <Typography sx={{ fontWeight: 500, mb: 0.5, fontSize: '1rem' }}>
                                              {member.name}
                                          </Typography>
                                          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', flexWrap: 'wrap', gap: '0 8px' }}>
                                              <span>{capitalize(member.relationship)}</span>
                                              <span>•</span>
                                              <span>{capitalize(member.gender)}</span>
                                              <span>•</span>
                                              <span>Born on {formatDate(member.dob)}</span>
                                              {member.tobacco && (
                                                  <>
                                                      <span>•</span>
                                                      <span>Tobacco User</span>
                                                  </>
                                              )}
                                              {member.pregnant && (
                                                  <>
                                                      <span>•</span>
                                                      <span>Pregnant</span>
                                                  </>
                                              )}
                                          </Typography>
                                      </Box>
                                      <Box 
                                          sx={{ 
                                              width: 30, 
                                              height: 30, 
                                              backgroundColor: '#f5f5f5',
                                              borderRadius: '50%',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              cursor: 'pointer'
                                          }}
                                          onClick={() => { editMember(member.name!) }}
                                      >
                                          <EditIcon sx={{ fontSize: 16, color: '#888' }} />
                                      </Box>
                                  </CardContent>
                              </Card>
                          );
                      })}
                      
                      <Button 
                          data-testid="add-new-person-button" 
                          variant="text" 
                          onClick={() => { addMember() }} 
                          startIcon={<AddIcon />}
                          sx={{ 
                              justifyContent: 'flex-start', 
                              color: 'primary.main', 
                              textTransform: 'none',
                              fontWeight: 500
                          }}
                      >
                          Add Another Member
                      </Button>
                  </Stack>
                  
                  {!Object.values(members).some(member => member.relationship === 'self') && (
                      <Typography variant="body2" color="warning.main" sx={{ mt: 2, fontWeight: 500 }}>
                          Please make sure that you have added yourself to your household. If you are shopping for someone else, you should set the primary policyholder&apos;s relationship as &quot;self&quot;.
                      </Typography>
                  )}
              </DialogContent>
              <DialogActions sx={{ 
                p: 2,
                borderTop: '1px solid #eee'
              }}>
                <Button
                  data-testid="continue-button"
                  variant="contained"
                  onClick={() => { save() }}
                  fullWidth
                >
                  Continue
                </Button>
              </DialogActions>
          </Dialog>
        );
    };

    return (
        <>
            {renderEditMemberDialog()}
            {renderHouseholdDialog()}
        </>
    );
}
export default HouseholdMembers;
